// Single source of truth for every map overlay layer in Fyrefly.
//
// Adding a new layer = adding an entry to `LAYER_CATALOG` here.
// Renderers, the toggle UI, and the layer-status store all read from this
// file, which means new layers slot in without touching plumbing.
//
// Layer groups define mutual-exclusion behavior (FF-style "exclusive
// overlay" pattern):
//   - 'heavy-overlay': only ONE layer in the group can be active at a
//                      time (turning one on auto-disables the others).
//                      Used for high-memory polygon layers.
//   - 'weather':       independent toggles, each is light enough to
//                      coexist with the others.
//   - 'ops':           independent toggles for operational data
//                      (own-ship, plan, navaids).
//
// When you add a layer:
//   1. Add the entry below.
//   2. Add the `LayerKey` to LayerStatusStore if you want loading/updated
//      tracking surfaced in the UI.
//   3. Implement the source/paint adders in MapView.tsx.
//   4. Wire it into `refreshBboxLayers` (or its own refresh function).

export type LayerGroup = 'heavy-overlay' | 'weather' | 'gfa' | 'ops' | 'fire-ops';

/**
 * Auth requirement for a layer.
 *  - 'public': anonymous access works
 *  - 'agol-nifc': needs a NIFC ArcGIS Online sign-in
 */
export type LayerAuth = 'public' | 'agol-nifc';

/**
 * Stable IDs used as React keys, persistence keys, and the discriminator
 * for current vs. requested visibility. NEVER renumber or rename — clients
 * may have these in localStorage.
 */
export type LayerId =
  // Heavy polygon overlays (mutually exclusive)
  | 'fires'        // NIFC WFIGS Interagency Perimeters Current (public)
  | 'fireguard'    // NIFC FireGuard fire detection (NIFC AGOL auth)
  | 'nirops'       // NIFC NIROPS infrared mapping (NIFC AGOL auth)
  | 'sua'          // FAA Special Use Airspace
  | 'viirs'        // NASA VIIRS heat detections (IR-style hot spots)
  | 'smoke'        // NOAA HRRR-Smoke surface concentration (future)
  // Weather (independent)
  | 'tfr'          // FAA TFRs (always-on default; light)
  | 'airmet'       // FAA G-AIRMET (legacy combined layer; superseded by
                   // the gfa-* group below; kept temporarily for users
                   // who had it on)
  | 'sigmet'       // FAA SIGMET (driven by wxhazards now; chip in bar)
  | 'cwa'          // FAA Center Weather Advisory (chip in bar)
  | 'wxhazards'    // Master toggle for the AIRMET/SIGMET/CWA hazard bar
  | 'wxcams'       // FAA WeatherCams sites (clickable for live image)
  | 'flightcat'    // Flight Category dot map (METAR + TAF, time-aware)
  | 'surfacewinds' // Wind barbs at each station (METAR + TAF)
  | 'ceilvis'      // Ceiling + Visibility numerical readout (combined)
  | 'temperature'  // OAT in C with optional dewpoint
  | 'dewspread'    // Temp − dewpoint spread (closeness-to-saturation)
  | 'pireps'       // Pilot Reports (turb / icing / wshear / urgent / cloud / wx)
  | 'nexrad'       // NEXRAD reflectivity (Iowa State Mesonet n0q tiles)
  | 'hrrr-smoke'   // NOAA HRRR-Smoke surface PM2.5 concentration
  | 'wind'         // Animated wind particles (NOAA GFS, altitude-selectable)
  // Graphical Forecasts for Aviation (GFA) hazards — split G-AIRMET
  // by hazard so a fire/HEMS/SAR pilot can toggle exactly the
  // hazards they care about onto the flight plan.
  | 'gfa-ifr'
  | 'gfa-mtnobscn'
  | 'gfa-ice'
  | 'gfa-turblo'
  | 'gfa-turbhi'
  | 'gfa-llws'
  | 'gfa-fzlvl'
  | 'gfa-sfcwnd'
  // Ops (independent)
  | 'waypoints'    // VOR / VORTAC navaids
  | 'fixes'        // Named IFR fixes (5-letter waypoints)
  | 'holdings'     // FAA NASR published holding patterns (HPF.txt)
  | 'sfcanalysis'  // NWS WPC surface analysis (fronts / highs / lows)
  | 'satgeocolor'  // NOAA GOES enhanced satellite (GeoColor)
  | 'satir'        // NOAA GOES color IR (cloud tops)
  | 'fuel100ll'    // Airports with 100LL avgas (FAA NASR)
  | 'fueljeta'     // Airports with Jet-A turbine fuel (FAA NASR)
  | 'terrain'      // Hillshade-based terrain advisor (red-tinted)
  | 'heliports'    // NASR heliports (opt-in for HEMS / fire-aviation / SAR)
  | 'dispatch'     // NIFC dispatch-zone polygons + boundary-crossing alert
  | 'obstacles'    // FAA DOF tall obstacles (towers, antennas, stacks, etc.)
  | 'repeaters'    // NIFC aviation-frequency repeater / receiver sites
  | 'ihogRings'    // Fire Traffic Area (FTA) — IHOG 12-NM contact + 7-NM exclusion rings around active incidents
  | 'dipSites'     // Recorded water-source dip sites (our org's data)
  | 'airports'     // FAA NASR airports (ICAO symbol layer — togglable via Aero panel)
  | 'airportDiagrams' // OSM aeroway taxiway/runway/apron polygons at high zoom
  | 'namedPasses'  // Sectional-chart-labelled VFR mountain passes
  | 'classBCD'     // FAA Class B / C / D controlled airspace polygons
  | 'airways'      // FAA ATS Route — Victor (V), Jet (J), RNAV (T/Q) centerlines
  | 'flightPlan'   // Active flight plan route
  | 'ownship'      // GPS ownship marker
  | 'breadcrumbs'  // Flight-log breadcrumb trail (recent GPS fixes)
  | 'rangeRings'   // Concentric distance rings around the own-ship
  | 'glideRing'    // Terrain-clipped emergency glide-ring around own-ship
  | 'circlingArea' // FAA TERPS circling-to-land area for the loaded IAP (advisory)
  | 'traffic'     // ADS-B traffic (internet source v1; GDL90 receiver later)
  // Fire Ops (independent, hidden from LayerSheet — toggle via Fire Ops panel)
  | 'fireMissionTfrs'       // Active TFRs from FAA, highlighted for fire ops context
  | 'fireMissionIncidents'  // Active large-fire incident locations from NIFC WFIGS
  | 'airport-da';           // Density altitude halo per airport (METAR + ISA fallback)

export interface LayerDef {
  id: LayerId;
  /** Short user-facing name (LayerSheet primary text). */
  label: string;
  /** Subtitle / cadence hint. */
  sub?: string;
  /** Mutual-exclusion grouping. */
  group: LayerGroup;
  /** Default state on first run. */
  defaultOn: boolean;
  /** Friendly description shown when the layer is part of a heavy group. */
  groupNote?: string;
  /**
   * If this layer is so heavy we should only fetch on opt-in, set
   * `lazy: true`. The renderer will skip auto-loading on map mount.
   */
  lazy?: boolean;
  /**
   * If non-null, the layer can be refreshed via the LayerSheet ↻ button.
   * The string is the LayerStatusStore key for loading/updated badges.
   */
  statusKey?: string;
  /**
   * Auth required to fetch this layer. Defaults to 'public'.
   * Layers gated on 'agol-nifc' are dimmed in the UI until the user signs
   * in via Settings (and after the boss provisions VITE_AGOL_CLIENT_ID).
   */
  auth?: LayerAuth;
  /**
   * When true, this entry is omitted from the Layers sheet. Its toggle
   * state still drives MapView visibility, but the user-facing control
   * lives somewhere else (e.g. Settings). Use for personal-preference
   * affordances that aren't really 'data layers' — breadcrumbs and
   * range rings, primarily.
   */
  hideFromSheet?: boolean;
}

/**
 * The catalog. Order here defines the order layers appear in the
 * LayerSheet within their group. The system is data-driven; do not
 * hand-roll switch statements.
 */
export const LAYER_CATALOG: Readonly<LayerDef[]> = [
  // ---- Heavy polygon overlays (mutually exclusive) -----------------------
  {
    id: 'fires',
    label: 'Wildfire Perimeters',
    sub: 'NIFC · 5 min',
    group: 'heavy-overlay',
    defaultOn: true,
    groupNote: 'Heavy overlay — replaces SUA / VIIRS / Smoke when active.',
    statusKey: 'fires',
  },
  {
    id: 'fireguard',
    label: 'FireGuard',
    sub: 'NIFC · satellite · sign-in',
    group: 'heavy-overlay',
    defaultOn: false,
    lazy: true,
    auth: 'agol-nifc',
    groupNote: 'Heavy overlay — NIFC FireGuard satellite fire detection (sign-in required).',
    statusKey: 'fireguard',
  },
  {
    id: 'nirops',
    label: 'NIROPS Infrared',
    sub: 'NIFC · nightly IR · sign-in',
    group: 'heavy-overlay',
    defaultOn: false,
    lazy: true,
    auth: 'agol-nifc',
    groupNote: 'Heavy overlay — NIFC nightly IR mapping, incident-specific (sign-in required).',
    statusKey: 'nirops',
  },
  {
    id: 'sua',
    label: 'Special Use Airspace',
    sub: 'MOA / R / W / P / A',
    group: 'heavy-overlay',
    defaultOn: false,
    lazy: true,
    groupNote: 'Heavy overlay — replaces Wildfires / VIIRS / Smoke when active.',
    statusKey: 'sua',
  },
  {
    id: 'viirs',
    label: 'VIIRS Heat Detections',
    sub: 'satellite · 12 h',
    group: 'heavy-overlay',
    defaultOn: false,
    lazy: true,
    groupNote: 'Heavy overlay — replaces Wildfires / SUA / Smoke when active.',
    statusKey: 'viirs',
  },
  // 'smoke' will join the heavy-overlay group once HRRR-Smoke ingestion
  // ships. Not in the catalog yet.

  // ---- Weather (independent) --------------------------------------------
  {
    id: 'tfr',
    label: 'TFRs',
    sub: 'live · 5 min',
    group: 'weather',
    defaultOn: true,
    statusKey: 'tfr',
  },
  // SIGMET — driven by the wxhazards bar's SIGMET chip now. Default ON
  // so when wxhazards turns on, SIGMET is included automatically.
  // Hidden from the main LayerSheet rows (filter applied in LayerSheet.tsx).
  {
    id: 'sigmet',
    label: 'SIGMET',
    sub: 'NWS · live',
    group: 'weather',
    defaultOn: true,
    statusKey: 'sigmet',
  },
  {
    // Density-altitude badge behind each airport dot. Boss request
    // 2026-05-25 21:28 PT — 'fire-aviation in summer over the Sierras /
    // Cascades / Rockies; DA matters more than absolute terrain
    // elevation for can-I-climb-out decisions.' Computed at field
    // elevation from the latest METAR (ISA fallback when METAR missing).
    id: 'airport-da',
    label: 'Density Altitude',
    sub: 'METAR · z≥8',
    group: 'weather',
    defaultOn: false,
    lazy: true,
  },
  {
    // NOAA HRRR-Smoke surface PM2.5 concentration. Ingested hourly via
    // scripts/smoke-ingest/smoke_ingest.py; colored per AQI bands
    // (yellow → orange → red → brown). Phase 1 (current) shows surface
    // concentration only; Phase 2 will add forecast time scrubber;
    // Phase 3 will add altitude selection. Backend: api/wx/smoke.ts.
    id: 'hrrr-smoke',
    label: 'HRRR Smoke',
    sub: 'NOAA · PM2.5 · turns Wind off',
    group: 'weather',
    defaultOn: false,
    lazy: true,
    statusKey: 'hrrr-smoke',
  },
  {
    // FAA WeatherCams — 932 sites across AK / CONUS / HI / Canada.
    // Clickable for the live image grid + 12-image history. Wiley
    // Anderson feedback 2026-05-09.
    id: 'wxcams',
    label: 'Weather Cameras',
    sub: 'FAA · z≥8',
    group: 'weather',
    defaultOn: false,
    lazy: true,
  },
  // Legacy combined G-AIRMET (mash of all hazards in one color). Kept
  // around so existing users who had it on don't lose anything during
  // the rollout of the GFA hazard split. Hidden in the LayerSheet UI
  // (the wxhazards master toggle replaces it). Will be removed after
  // a couple of weeks of dual operation.
  {
    id: 'airmet',
    label: 'G-AIRMET (legacy)',
    sub: 'NWS · 6 h',
    group: 'weather',
    defaultOn: false,
    statusKey: 'airmet',
  },
  // NWS WPC Surface Analysis — transparent PNG overlay showing fronts,
  // highs, lows, and isobars across CONUS. Auto-issued every 3 hours.
  // Member of heavy-overlay so it's mutually exclusive with the other
  // big rasters (NEXRAD, Visible Sat, IR Sat, fires, etc.). Boss
  // feedback 2026-05-10: avoid stacking memory-heavy CONUS-wide
  // imagery on top of each other.
  {
    id: 'sfcanalysis',
    label: 'Surface Analysis',
    sub: 'NWS WPC · fronts · 3 h',
    group: 'heavy-overlay',
    defaultOn: false,
    lazy: true,
    groupNote: 'Heavy overlay — replaces other big imagery layers when active.',
    statusKey: 'sfcanalysis',
  },
  // NOAA GOES-East visible satellite (channel 02, 1 km native).
  // Daytime-only — ramp this up alongside the IR layer to get a
  // GeoColor-equivalent viewing experience.
  {
    id: 'satgeocolor',
    label: 'Visible Satellite',
    sub: 'NOAA GOES-East · 1 km · day only',
    group: 'heavy-overlay',
    defaultOn: false,
    lazy: true,
    groupNote: 'Heavy overlay — replaces other big imagery layers when active.',
    statusKey: 'satgeocolor',
  },
  // Color IR Satellite — cloud-top temperature visualization. Always
  // available regardless of time of day; ideal for identifying
  // convective tops at night.
  {
    id: 'satir',
    label: 'IR Satellite',
    sub: 'NOAA GOES-East · cloud tops',
    group: 'heavy-overlay',
    defaultOn: false,
    lazy: true,
    groupNote: 'Heavy overlay — replaces other big imagery layers when active.',
    statusKey: 'satir',
  },
  // Terrain advisor — hillshade rendering of AWS Terrarium DEM tiles
  // tinted red/orange so high terrain reads as a hazard color. Boss
  // feedback 2026-05-10: 'Red, yellow, or transparent for how close
  // you are to terrain'. v1 ships hillshade-by-elevation; the
  // dynamic 'compare to aircraft altitude' shader is a follow-up.
  // Memory-bounded: terrain DEM tiles are small but tile a large
  // area, so it's in heavy-overlay alongside the satellite stack.
  {
    id: 'terrain',
    label: 'Terrain Advisor',
    sub: 'AWS DEM · high terrain shaded',
    group: 'heavy-overlay',
    defaultOn: false,
    lazy: true,
    groupNote: 'Heavy overlay — replaces other big imagery layers when active.',
  },
  // Master toggle: shows the AIRMET/SIGMET/CWA hazard bar at the
  // bottom of the map. The bar lets the user pick which hazard
  // family/sub-hazards to show plus a time scrubber. Selecting
  // hazards inside the bar drives the per-family layer visibility.
  {
    id: 'wxhazards',
    label: 'AIRMET / SIGMET / CWA',
    sub: 'NWS hazards · hazard bar',
    group: 'weather',
    defaultOn: false,
    lazy: true,
    statusKey: 'gfa',
  },
  // Center Weather Advisory — surfaced via the hazard bar's CWA chip.
  // Hidden in the main LayerSheet (no top-level toggle).
  {
    id: 'cwa',
    label: 'Center Weather Advisory',
    sub: 'NWS · per-ARTCC',
    group: 'weather',
    defaultOn: true,
    statusKey: 'cwa',
  },
  // Flight Category dot map — colored station dots (VFR/MVFR/IFR/LIFR)
  // from METAR (now) + TAF (forecast). Drives off the same time slider
  // as the hazard bar so the dots evolve as the user scrubs through
  // forecast horizons. The differentiator vs ForeFlight: FF hides this
  // in their Imagery viewer, separated from the flight plan; ours
  // overlays directly on top of the route.
  {
    id: 'flightcat',
    label: 'Flight Category',
    sub: 'METAR + TAF · dot map',
    group: 'weather',
    defaultOn: false,
    lazy: true,
    statusKey: 'flightcat',
  },
  // Surface Winds — wind barbs at each station, rotated by direction
  // and scaled to speed. METAR for now / TAF for forecast frames.
  // Same data source as Flight Category (one fetch, multiple
  // visualizations).
  {
    id: 'surfacewinds',
    label: 'Surface Winds',
    sub: 'METAR + TAF · wind barbs',
    group: 'weather',
    defaultOn: false,
    lazy: true,
    statusKey: 'flightcat',
  },
  // Ceiling + Visibility — numerical readout next to a neutral marker
  // at each station. Boss feedback 2026-05-10: separate color-coded
  // ceiling and visibility layers were too easy to confuse with the
  // flight-cat dots. Showing the actual numbers ("18 / 10" = ceiling
  // 1800 ft AGL, vis 10 sm) is more useful and unambiguous.
  {
    id: 'ceilvis',
    label: 'Ceiling / Visibility',
    sub: 'METAR + TAF · numerical readout',
    group: 'weather',
    defaultOn: false,
    lazy: true,
    statusKey: 'flightcat',
  },
  // Temperature — OAT in °C next to a neutral marker.
  {
    id: 'temperature',
    label: 'Temperature',
    sub: 'METAR · OAT °C',
    group: 'weather',
    defaultOn: false,
    lazy: true,
    statusKey: 'flightcat',
  },
  // Dewpoint Spread — (T − Td) in °C. Small spread → close to
  // saturation (fog/low cloud risk). Colored: red < 2°C, yellow 2–5,
  // green ≥ 5.
  {
    id: 'dewspread',
    label: 'Dewpoint Spread',
    sub: 'METAR · T − Td (°C)',
    group: 'weather',
    defaultOn: false,
    lazy: true,
    statusKey: 'flightcat',
  },
  // PIREPs — Pilot Reports of in-flight conditions. The closest thing
  // to ground-truth weather we have, especially for fire/HEMS/SAR
  // pilots flying outside the ATC system in marginal conditions.
  {
    id: 'pireps',
    label: 'PIREPs',
    sub: 'pilot reports · last 2h',
    group: 'weather',
    defaultOn: false,
    lazy: true,
    statusKey: 'pireps',
  },
  // Radar reflectivity. ForeFlight-style three-tier picker (boss 2026-05-21):
  //   Composite (default) — MRMS multi-sensor QC'd composite via NCEP.
  //   Classic             — NEXRAD Level III base reflectivity (n0q).
  //   Low Tilt            — MRMS Seamless Hybrid-Scan (closest-to-ground beam).
  // All three render through a single 'nexrad' layer/source on the map; the
  // variant is picked in uiStore.nexradVariant and the radio strip lives
  // under the row in the layer picker.
  {
    id: 'nexrad',
    label: 'Radar',
    sub: 'NEXRAD / MRMS · 5 min',
    group: 'heavy-overlay',
    defaultOn: false,
    lazy: true,
    groupNote: 'Heavy overlay — replaces other big imagery layers when active.',
    statusKey: 'nexrad',
  },

  // ---- GFA hazards (independent toggles, share one source) ------------
  // Eight hazards from the same /api/wx/gfa fetch, rendered as
  // separate map layers. NOT shown in the main LayerSheet — they're
  // driven by the AIRMET sub-row of the wxhazards bar (WxHazardBar
  // component). Default is ON so that when the user enables the
  // 'wxhazards' master toggle for the first time, all 8 hazards are
  // visible without an extra setup step.
  {
    id: 'gfa-ifr',
    label: 'IFR',
    sub: 'AIRMET · ceiling < 1000 / vis < 3sm',
    group: 'gfa',
    defaultOn: true,
    lazy: true,
    statusKey: 'gfa',
  },
  {
    id: 'gfa-mtnobscn',
    label: 'Mountain Obsc',
    sub: 'AIRMET · MTNS OBSC',
    group: 'gfa',
    defaultOn: true,
    lazy: true,
    statusKey: 'gfa',
  },
  {
    id: 'gfa-ice',
    label: 'Icing',
    sub: 'AIRMET · severity + altitude band',
    group: 'gfa',
    defaultOn: true,
    lazy: true,
    statusKey: 'gfa',
  },
  {
    id: 'gfa-turblo',
    label: 'Turb Lo',
    sub: 'AIRMET · below FL180',
    group: 'gfa',
    defaultOn: true,
    lazy: true,
    statusKey: 'gfa',
  },
  {
    id: 'gfa-turbhi',
    label: 'Turb Hi',
    sub: 'AIRMET · FL180 – FL450',
    group: 'gfa',
    defaultOn: true,
    lazy: true,
    statusKey: 'gfa',
  },
  {
    id: 'gfa-llws',
    label: 'LLWS',
    sub: 'AIRMET · ≥ 20 KT below 2000 AGL',
    group: 'gfa',
    defaultOn: true,
    lazy: true,
    statusKey: 'gfa',
  },
  {
    id: 'gfa-fzlvl',
    label: 'FZ Level',
    sub: 'AIRMET · contour',
    group: 'gfa',
    defaultOn: true,
    lazy: true,
    statusKey: 'gfa',
  },
  {
    id: 'gfa-sfcwnd',
    label: 'Sfc Wind',
    sub: 'AIRMET · sustained ≥ 30 KT',
    group: 'gfa',
    defaultOn: true,
    lazy: true,
    statusKey: 'gfa',
  },

  // ---- Ops (independent) ------------------------------------------------
  {
    // Navaids — moved to Aero panel (hideFromSheet: true). Toggle still
    // lives here so the LayerCatalog-driven visibility plumbing works;
    // the user-facing control is the Aero panel knob cluster.
    id: 'waypoints',
    label: 'Navaids',
    sub: 'VOR / VOR-DME / VORTAC / NDB',
    group: 'ops',
    defaultOn: true,
    hideFromSheet: true,
  },
  {
    // Named IFR fixes — moved to Aero panel (hideFromSheet: true).
    id: 'fixes',
    label: 'Fixes',
    sub: '5-letter named waypoints · z≥8',
    group: 'ops',
    defaultOn: false,
    hideFromSheet: true,
  },
  {
    // Published holding patterns from FAA NASR HPF.txt — racetracks
    // drawn at the fix's lat/lon along the published inbound course.
    // Zoom-gated at z>=8. Off by default; available via the Aero panel.
    id: 'holdings',
    label: 'Holds',
    sub: 'Published holding patterns · z≥8',
    group: 'ops',
    defaultOn: false,
    hideFromSheet: true,
  },
  // 100LL fuel availability dots (NASR-truthy via airports.json).
  {
    id: 'fuel100ll',
    label: 'Fuel: 100LL',
    sub: 'NASR · avgas-equipped fields',
    group: 'ops',
    defaultOn: false,
    lazy: true,
  },
  // Jet-A fuel availability dots.
  {
    id: 'fueljeta',
    label: 'Fuel: Jet-A',
    sub: 'NASR · turbine-fuel fields',
    group: 'ops',
    defaultOn: false,
    lazy: true,
  },
  {
    // Heliports — moved to Aero panel (hideFromSheet: true).
    // HEMS / fire-aviation / SAR pilots opt in here. Wiley Anderson
    // feedback 2026-05-09.
    id: 'heliports',
    label: 'Heliports',
    sub: 'NASR · z≥10',
    group: 'ops',
    defaultOn: false,
    lazy: true,
    hideFromSheet: true,
  },
  {
    // Fire dispatch zones — each polygon is the operational area of
    // an interagency dispatch center. Pilots crossing a boundary need
    // to switch to the new dispatch contact freq, so the layer also
    // drives a boundary-crossing alert via alertTriggers.
    id: 'dispatch',
    label: 'Dispatch zones',
    sub: 'NIFC · fire dispatch boundaries',
    group: 'ops',
    defaultOn: false,
    lazy: true,
  },
  {
    // IHOG proximity rings drawn around active incidents in the org's
    // incidents store. Outer ring = contact (12 NM default, amber
    // dashed). Inner ring = exclusion (7 NM default, red solid). Each
    // incident may override the defaults via ihog_contact_nm /
    // ihog_exclusion_nm columns. Pairs with the IHOG_APPROACH alert
    // (alertTriggers Trigger 5) so the pilot sees the rings before
    // they trip an alert.
    //
    // Visually distinct from TFRs (TFRs are red filled polygons with
    // crosshatch; FTA rings are stroked-only). Both can be on at
    // once — the TFR is the FAA's actual airspace; the FTA ring is
    // the procedural buffer that often extends beyond it.
    //
    // Internal id stays `ihogRings` because the radii come from the
    // IHOG (Interagency Helicopter Operations Guide). User-facing
    // label is "Fire Traffic Area" per industry convention (Wiley
    // correction 2026-05-13).
    id: 'ihogRings',
    label: 'Fire Traffic Area',
    sub: 'Active incidents · 12 / 7 NM',
    group: 'ops',
    defaultOn: true,
  },
  {
    // Dip sites — water-source locations recorded by pilots in this
    // org for retardant / water drops. Reusable across many dips at
    // the same source. Color-coded by ownership class so the pilot
    // sees at a glance whether the source is on federal (green) or
    // private (amber) land. No public dataset exists; this is purely
    // our org's data.
    id: 'dipSites',
    label: 'Dip sites',
    sub: 'Org water sources · color by ownership',
    group: 'ops',
    defaultOn: true,
  },
  {
    // FAA Digital Obstacle File — towers, antennas, stacks, buildings
    // and other 'tall stuff' that flying through is a bad idea.
    // Filtered to >= 200 ft AGL by default to cut clutter; settings
    // can pull the threshold down to 100 ft (or even 50) for low-
    // altitude helicopter / fire ops.
    id: 'obstacles',
    label: 'Tall obstacles',
    sub: 'FAA DOF · ≥200 ft AGL',
    group: 'ops',
    defaultOn: false,
    lazy: true,
  },
  {
    // NIFC aviation-frequency repeater / receiver sites — parsed
    // from the 2013 National Interagency Aviation Frequency Guide.
    // Each site is a mountain-top receiver/repeater that a fire-
    // dispatch center uses for Flt Following / Air Guard / Local
    // channels. Tap a site to see freq, tone, and parent center.
    id: 'repeaters',
    label: 'Fire repeaters',
    sub: 'NIFC · dispatch-center freqs',
    group: 'ops',
    defaultOn: false,
    lazy: true,
  },
  {
    // FAA NASR airports — the ICAO-symbol layer that always loads from
    // the bbox endpoint. This entry gives the Aero panel a toggle
    // without moving it into the LayerSheet. Default ON; hideFromSheet
    // because the Aero panel is the user-facing control.
    id: 'airports',
    label: 'Airports',
    sub: 'FAA NASR · ICAO symbology',
    group: 'ops',
    defaultOn: true,
    hideFromSheet: true,
  },
  {
    // Airport diagrams — OSM aeroway features (runways, taxiways,
    // aprons, helipads, hangars) drawn as a vector overlay at high
    // zoom levels (z >= 14). Renders ForeFlight-style taxiway
    // diagrams without requiring a published FAA APD. Data fetched
    // on-demand from /api/airport-diagram (OSM-via-Overpass with
    // persistent Blob cache).
    // Boss request 2026-05-18 22:42 PT. See data/taxiway-diagrams-plan.md.
    id: 'airportDiagrams',
    label: 'Airport diagrams',
    sub: 'OSM taxiways · zoom in close',
    group: 'ops',
    defaultOn: true,
  },
  {
    // Animated wind particles at selectable altitude (NOAA GFS,
    // refreshed every 6h via iMac launchd cron). Shipped 2026-05-20.
    // Backend: api/wind-raster.ts + scripts/wind-ingest/.
    // Rendering: src/map/wind/windParticleLayer.ts (WebGL custom layer).
    id: 'wind',
    label: 'Wind',
    sub: 'NOAA GFS · alt-selectable · turns Smoke off',
    // Heavy: continuous WebGL particle simulation (~1500 sprites/frame on
    // GPU) plus 720×361 PNG raster reload on altitude/time change. Competes
    // for GPU and raster bandwidth with terrain/nexrad/satellite — mutex with
    // them so the device doesn't choke on a stack of heavy raster + shader
    // layers. (2026-05-21 boss audit.)
    group: 'heavy-overlay',
    defaultOn: false,
    groupNote: 'Heavy overlay — replaces NEXRAD / Satellite / Terrain / fire layers when active.',
  },
  {
    // Named VFR mountain passes from sectional charts. Used as a
    // visual reference and as bias points for the terrain-clearance
    // auto-router (/api/route/pass-finder). Static corpus in
    // api/data/namedPasses.ts.
    // Boss request 2026-05-19 07:38 PT (Wiley auto-route ask).
    id: 'namedPasses',
    label: 'Mountain passes',
    sub: 'sectional · VFR crossings',
    group: 'ops',
    defaultOn: false,
  },
  {
    // FAA Class B / C / D controlled airspace. Bbox-filtered via the
    // /api/airspace-class proxy (same AGOL org as SUA). Lazy: the
    // national B/C/D polygon set is sizable; only load when the pilot
    // opts in from the Aero panel. hideFromSheet: lives in Aero panel.
    id: 'classBCD',
    label: 'Class B / C / D',
    sub: 'FAA controlled airspace',
    group: 'ops',
    defaultOn: false,
    lazy: true,
    statusKey: 'classBCD',
    hideFromSheet: true,
  },
  {
    // FAA ATS Route centerlines — Victor (V), Jet (J), and RNAV (T/Q)
    // airways. Proxied via /api/airways from the FAA AIS ArcGIS
    // FeatureServer. Bbox-filtered; color-coded by ROUTE_CLASS.
    // hideFromSheet: controlled by the Aero panel.
    id: 'airways',
    label: 'Airways',
    sub: 'FAA ATS · Victor / Jet / RNAV',
    group: 'ops',
    defaultOn: false,
    lazy: true,
    statusKey: 'airways',
    hideFromSheet: true,
  },
  {
    id: 'flightPlan',
    label: 'Flight plan',
    sub: 'active route',
    group: 'ops',
    defaultOn: true,
  },
  {
    id: 'ownship',
    label: 'Own-ship',
    sub: 'GPS marker',
    group: 'ops',
    defaultOn: true,
  },
  {
    id: 'breadcrumbs',
    label: 'Breadcrumbs',
    sub: 'recorded GPS track',
    group: 'ops',
    defaultOn: true,
    hideFromSheet: true,
  },
  {
    id: 'rangeRings',
    label: 'Range rings',
    sub: 'distance rings around own-ship',
    group: 'ops',
    defaultOn: true,
    hideFromSheet: true,
  },
  {
    id: 'glideRing',
    label: 'Glide ring',
    sub: 'emergency reach · wind + terrain',
    group: 'ops',
    defaultOn: false,
  },
  {
    // Circling-to-land area for the loaded IAP — green stadium polygon
    // sized for the active aircraft category (AIM 5-4-29). Sector
    // restrictions parsed from the plate notes are hatched out in magenta.
    // Default ON: only paints when an IAP is loaded into the active plan,
    // so it's effectively a no-op outside instrument approach setup.
    // Advisory — verify against the published plate.
    id: 'circlingArea',
    label: 'Circling area',
    sub: 'IAP · CAT-sized · advisory',
    group: 'ops',
    defaultOn: true,
  },
  {
    id: 'traffic',
    label: 'ADS-B traffic',
    sub: 'live aircraft (internet source)',
    group: 'ops',
    defaultOn: false,
    lazy: true,
    statusKey: 'traffic',
  },

  // ---- Fire Ops (independent, hidden from LayerSheet) ------------------
  // Controlled by the Fire Ops panel (FireOpsPanel component).
  {
    id: 'fireMissionTfrs',
    label: 'Fire TFRs',
    sub: 'FAA · live · 5 min',
    group: 'fire-ops',
    defaultOn: false,
    lazy: true,
    statusKey: 'tfr',   // shares the tfr status key for loading/updated badge
    hideFromSheet: true,
  },
  {
    id: 'fireMissionIncidents',
    label: 'Active Fires',
    sub: 'NIFC WFIGS · ≥100 ac · 10 min',
    group: 'fire-ops',
    defaultOn: false,
    lazy: true,
    hideFromSheet: true,
  },
];

/** Lookup by ID. */
const BY_ID: Record<LayerId, LayerDef> = (() => {
  const m = {} as Record<LayerId, LayerDef>;
  for (const l of LAYER_CATALOG) m[l.id] = l;
  return m;
})();

export function getLayerDef(id: LayerId): LayerDef {
  return BY_ID[id];
}

export function getLayersInGroup(group: LayerGroup): LayerDef[] {
  return LAYER_CATALOG.filter((l) => l.group === group && !l.hideFromSheet);
}

/** True if `group` enforces single-active behavior. */
export function isExclusiveGroup(group: LayerGroup): boolean {
  return group === 'heavy-overlay';
}
